/**
 * THE SOURCE GRAPH. Art builds every source's graph itself and hands the analysis node to BOTH listeners:
 * the app-wide StereoAnalyser (the spectrum every world reads) and the hearing (audio/hearing.ts). (The
 * engine's StereoAnalyser can wire a stream or a media element on its own, but then the graph is private to
 * it and nothing else can hear that audio — the student needs the samples, not the spectrum.)
 */
import { Engine } from '@syqel/ota-client'
import { audioDiscontinuity, hearSource } from './hearing'

export const stereo = new Engine.StereoAnalyser()

let ownedStream: MediaStream | null = null // a stream whose graph we built — its tracks stop when the source changes

/** Close an AudioContext at most once. Reconnecting a cast (e.g. two senders churning the receiver's
 *  discovery) otherwise re-closes an already-closed context, throwing an uncaught "Cannot close a closed
 *  AudioContext" (InvalidStateError) that wedges the receiver's audio graph → the TV goes silent. */
export function closeCtx(ctx: AudioContext | null): void {
  if (!ctx || ctx.state === 'closed') return
  // On a rapid cast reconnect two teardowns can race: the first close() moves the context to 'closing',
  // the second then throws InvalidStateError ("Cannot close a closed AudioContext"). Some WebView builds
  // throw that SYNCHRONOUSLY (before the promise exists), so `.catch` alone misses it — swallow both forms.
  try { void ctx.close().catch(() => undefined) } catch { /* already closing/closed */ }
}

/** Point both listeners at a new source's node (closing the previous source's context). */
export function analyse(ctx: AudioContext, node: AudioNode, stream: MediaStream | null = null): void {
  if (ownedStream && ownedStream !== stream) ownedStream.getTracks().forEach((t) => t.stop())
  ownedStream = stream
  stereo.attachSource(ctx, node) // detaches (and closes) whatever context came before
  hearSource(ctx, node)
}

/** autoplay policy can leave a fresh context suspended (visuals animate off nothing, speakers stay
 *  silent) — rescue it on the next user gesture. */
function resumeOrRescue(ctx: AudioContext): void {
  void ctx.resume()
  if (ctx.state !== 'suspended') return
  const rescue = (): void => {
    void ctx.resume()
    window.removeEventListener('pointerdown', rescue)
    window.removeEventListener('keydown', rescue)
  }
  window.addEventListener('pointerdown', rescue)
  window.addEventListener('keydown', rescue)
}

/** A live stream (an input device, a media element's captured copy): analysed, never re-output (it would echo). */
export function analyseStream(stream: MediaStream): void {
  const ctx = new AudioContext()
  analyse(ctx, ctx.createMediaStreamSource(stream), stream)
  resumeOrRescue(ctx)
}

/**
 * A playing media element. PREFERRED: the element keeps playing NATIVELY to the speakers and we analyse
 * a CAPTURED COPY of its stream (createMediaElementSource would hijack its whole output through the
 * context — one suspended context and the visuals move over silent speakers). Where captureStream does
 * not exist (Safari / WKWebView — the macOS desktop app) the element is routed through the context and
 * out to the speakers. An element can feed only ONE context, ever — loadFile gives each file its own.
 * `current` answers whether the source this element belongs to is still the session's (a late 'canplay'
 * after the user moved on must not wire it).
 */
export function analyseMedia(media: HTMLMediaElement, current: () => boolean): void {
  type Capturable = HTMLMediaElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream }
  const m = media as Capturable
  media.muted = false
  media.volume = 1
  // a seek is a jump in the audio without a new node: the voice orbs start over (the element is this source's
  // alone — loadFile makes a fresh one per file — so the listener goes with it)
  media.addEventListener('seeked', () => { if (current()) audioDiscontinuity() })
  const cap = m.captureStream ?? m.mozCaptureStream
  if (!cap) {
    const ctx = new AudioContext()
    const src = ctx.createMediaElementSource(media)
    src.connect(ctx.destination)
    analyse(ctx, src)
    resumeOrRescue(ctx)
    return
  }
  const tryWire = (): boolean => {
    const stream = cap.call(media)
    if (stream.getAudioTracks().length === 0) return false // tracks appear once playback starts
    analyseStream(stream)
    return true
  }
  if (tryWire()) return
  const onReady = (): void => {
    media.removeEventListener('canplay', onReady)
    if (current() && !tryWire()) console.warn('[syqel] file has no audio track to analyse')
  }
  media.addEventListener('canplay', onReady)
}
